import { useState, useEffect, useRef } from "react";
import "./FormContent.css";
import { useDispatch, useSelector } from "react-redux";
import { selectEditingForm, selectEditingQuestionId, selectSelectedComment, selectUIStates, selectUser, selectViewingFormThemeSettings, setSelectedComment, setUIStates, setViewingFormThemeSettings } from "../../redux/features/appData";
import { iconPath, Question, QuestionComment, QuestionType } from "effno-core";
import { FormContentShort } from "./FormContentShort";
import { ICONS } from "../shared/media";
import { FormContentEmail } from "./FormContentEmail";
import { FormContentMultiple } from "./FormContentMultiple";
import { FormContentEnd } from "./FormContentEnd";
import { FormContentWebsite } from "./FormContentWebsite";
import { FormContentNumber } from "./FormContentNumber";
import { FormContentPhone } from "./FormContentPhone";
import { FormContentContact } from "./FormContentContact";
import { FormContentNPS } from "./FormContentNPS";
import { FormContentScore } from "./FormContentScore";
import { FormContentRating } from "./FormContentRating";
import { FormThemeSettings } from "../question-design/QuestionDesign";
import { lightenColor } from "../shared/uiHelpers";
import { Comment } from "../comment/Comment";
import { PublicDetails } from "../response-content/ResponseContent";
import { CommentAvatar } from "../comment/CommentAvatar";
import { FormContentDate } from "./FormContentDate";
import { FormContentSection } from "./FormContentSection";
import { ArrowRight, Laptop, Smartphone } from "lucide-react";
import { FormContentLong } from "./FormContentLong";

type FormContentProps = {
}

export const FormContent = ({} : FormContentProps) => {

    const dispatch = useDispatch();

    const storeUser = useSelector(selectUser);
    const editingForm = useSelector(selectEditingForm);
    const editingQuestionId = useSelector(selectEditingQuestionId);
    const viewingFormThemeSettings = useSelector(selectViewingFormThemeSettings);
    const uiStates = useSelector(selectUIStates);
    const selectedComment = useSelector(selectSelectedComment);

    const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);

    // const [selectedComment, setSelectedComment] = useState<QuestionComment | undefined>(undefined);
    const [containerSize, setContainerSize] = useState({width: 1, height: 1});
    const commentRef = useRef(null);
    
    const [commentXP, setCommentXP] = useState<number | undefined>(undefined);
    const [commentYP, setCommentYP] = useState<number | undefined>(undefined);
    const [hoveringCommentId, setHoveringCommentId] = useState<string | undefined>(undefined);
    const [commentTimeoutId, setCommentTimeoutId] = useState<NodeJS.Timeout | undefined>(undefined);
    // const [viewingComment, setViewingComment] = useState(false);

    useEffect(() => {
        window.addEventListener("resize", eventResize);
        eventResize();
        
        return () => window.removeEventListener("resize", eventResize);
    }, []);

    useEffect(() => {
        if (!editingQuestionId) {
            setEditingQuestion(null);
        }

        if (editingForm && !viewingFormThemeSettings) {
            dispatch(setViewingFormThemeSettings(
                JSON.parse(editingForm?.formTheme?.settings || "{}") as FormThemeSettings
            ));
        }

        if (editingQuestionId && editingForm) {
            const question = editingForm.questions.find((question : Question) => question.id === editingQuestionId);

            if (question) {
                setEditingQuestion(question);
            }

            // dispatch(setViewingFormThemeSettings(
            //     JSON.parse(editingForm?.formTheme?.settings || "{}") as FormThemeSettings
            // ));
        }
    }, [editingForm, editingQuestionId]);

    useEffect(() => {
        //
    }, [viewingFormThemeSettings]);

    useEffect(() => {

        console.log("[FormContent] selectedComment: ", selectedComment);

        if (selectedComment) {
            setCommentXP(selectedComment.xpercent);
            setCommentYP(selectedComment.ypercent);
        }
    }, [selectedComment]);

    const eventResize = () => {
        const current: any = commentRef.current;

        if (current) {
          setContainerSize({
            width: current.offsetWidth,
            height: current.offsetHeight,
          });
        }
      };

    const handleCreateComment = (e: any) => {
        // alert("Creating comment");

        const rect = e.currentTarget.getBoundingClientRect();

        // Calculate the relative X and Y as percentages
        // setCommentX(e.clientX - rect.left);
        // setCommentY(e.clientY - rect.top);

        const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
        const yPercent = ((e.clientY - rect.top) / rect.height) * 100;
        // console.log(`[ResponseContent/handleComment] Relative X: ${xPercent.toFixed(2)}%, Relative Y: ${yPercent.toFixed(2)}%`);

        setCommentXP(xPercent);
        setCommentYP(yPercent);
        dispatch(setUIStates({
            ...uiStates,
            viewingComment: true
        }));
        dispatch(setSelectedComment(undefined));

      
    }

    const handleOpenComment = (comment: QuestionComment) => {
        // alert("Opening comment");

        // console.log("[ResponseContent] handleOpenComment: ", comment);

        dispatch(setUIStates({
            ...uiStates,
            viewingComment: true
        }));
        dispatch(setSelectedComment({...comment}));
        setCommentXP(comment.xpercent);
        setCommentYP(comment.ypercent);
    }

    const handleCommentMouseEnter = (comment: QuestionComment) => {
        setHoveringCommentId(comment.id);

        const timeoutId = setTimeout(() => {
            setHoveringCommentId(undefined);
            handleOpenComment(comment);
        }, 100);

        setCommentTimeoutId(timeoutId);
    }

    const handleCommentMouseLeave = () => {
        if (commentTimeoutId) {
            clearTimeout(commentTimeoutId);
        }

        setHoveringCommentId(undefined);
    }

    const parentComments = editingQuestion ? editingQuestion.comments.filter((comment) => !comment.parentQuestionCommentId) : [];
    const brightness = viewingFormThemeSettings?.imageBrightness || 1.0;

    const formThemeStyle = { 
        "--ftBackgroundColor": viewingFormThemeSettings?.backgroundColor || "var(--default-ftBackgroundColor)",
        "--ftQuestionColor": viewingFormThemeSettings?.questionColor || "var(--default-ftQuestionColor)",
        "--ftAnswerColor": viewingFormThemeSettings?.answerColor || "var(--default-ftAnswerColor)",
        "--ftAnswerBackgroundColor": viewingFormThemeSettings?.answerColor ?
            lightenColor(viewingFormThemeSettings?.answerColor, 80) :
            "var(--default-ftAnswerBackgroundColor)",
        "--ftFont": viewingFormThemeSettings?.font || "var(--default-ftFont)",
        "--ftButtonColor": viewingFormThemeSettings?.buttonColor || "var(--default-ftButtonColor)",
        "--ftButtonTextColor": viewingFormThemeSettings?.buttonTextColor || "var(--default-ftButtonTextColor)",
        "--ftBackgroundImage": `url(${viewingFormThemeSettings?.backgroundImage || ""})`, 
        "--ftImageBrightness": `brightness(${brightness})`,
        "--ftImageOpacity": `${brightness}`,
    } as React.CSSProperties

    const questionOrder = editingForm?.questionOrder ? JSON.parse(editingForm.questionOrder) : [];
    const questionNumber = editingQuestion ? questionOrder.indexOf(editingQuestion.id) + 1 : 0;
    
    // console.log("[FormContent] editingQuestion: ", editingQuestion);
    // console.log("[FormContent] editingQuestionId: ", editingQuestionId);
    console.log("[FormContent] viewingFormSettings: ", viewingFormThemeSettings);
    // console.log("[FormContent] formThemeStyle: ", formThemeStyle);

//    console.log("[FormContent] containerSize: ", containerSize);

    return (
        <>
            <div 
                className="form-content-design"
                style={formThemeStyle}
            >
                {uiStates.commentMode && !uiStates.viewingComment &&
                    <div 
                        className="form-content-floating-comments"
                        onClickCapture={(e: any) => {
                            
                            if (!uiStates.viewingComment) {
                                e.preventDefault();
                                e.stopPropagation();
                                handleCreateComment(e);
                            }
                            return;
                            
                        }}
                    >
                        {}
                    </div>
                }
                
                <div 
                    className="response-content-wrapper"
                    ref={commentRef}
                    // className={"response-content-wrapper-comments" + (uiStates.commentMode ? " form-content-cursor-crosshair" : "")}
                    // style={(uiStates.commentMode) ? {cursor: "crosshair"} : {}}
                >
                    {/* <div 
                        className={"response-content-wrapper-comments-inner " + (uiStates.commentMode ? "form-content-cursor-crosshair" : "")}
                        style={(uiStates.commentMode) ? 
                            {cursor: "crosshair !important"} 
                            : 
                            {}}
                        onClickCapture={(e: any) => {
                            if (uiStates.commentMode) {
                                if (!uiStates.viewingComment) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleCreateComment(e);
                                }
                                return;
                            }
                        }}
                    > */}
                    

                        <div 
                            className={uiStates.mobileLayout ? "form-content-mobile" : "form-content-laptop"}
                            style={(editingQuestion?.type === QuestionType.CONTACT || editingQuestion?.type === QuestionType.MULTIPLE) ?
                                    {overflowY: "auto", scrollbarWidth: "none"} : {}
                            }
                        >
                            <div 
                                className="form-content-container"
                                style={{
                                    ...{fontSize: uiStates.mobileLayout ? ".75rem" : "1rem"}, 
                                    ...{alignItems: (editingQuestion?.type !== QuestionType.CONTACT && editingQuestion?.type !== QuestionType.MULTIPLE) ?
                                        "center" : "unset"},
                                    // ...(uiStates.commentMode ? {aspectRatio: "16 /9"} : {})
                                }}          
                            >
                                <div 
                                    className="form-content-container-inside"
                                    // style={uiStates.mobileLayout ? {marginBlock: "auto"} : {}}
                                >
                                    {(editingQuestion && editingQuestion?.type !== QuestionType.END &&
                                        editingQuestion?.type !== QuestionType.SECTION) &&
                                        
                                        <>
                                            {editingForm?.formSettings?.questionNumbers &&
                                                <div 
                                                    className={uiStates.mobileLayout ? 
                                                        "form-content-pointer-mobile" : "form-content-pointer"}
                                                >
                                                    <div 
                                                        className="form-content-pointer-arrow"
                                                        style={{paddingTop: uiStates.mobileLayout ? "4px" : "6px"}}
                                                    >
                                                        <div className="form-content-pointer-arrow-container">
                                                            {questionNumber}
                                                            <ArrowRight size={20} color="var(--ftQuestionColor)" />
                                                            {/* <img src={iconPath(ICONS.ARROW, storeUser)} /> */}
                                                        </div>
                                                    </div> 
                                                </div>
                                            }
                                            <div 
                                                className="form-content-required"
                                                style={{paddingTop: uiStates.mobileLayout ? "2px" : "6px"}}
                                            >
                                                {editingQuestion?.required && <span>*</span>}
                                            </div>
                                        </>
                                    }


                        
                                    {(
                                        () => {
                                            switch(editingQuestion?.type) {
                                                case QuestionType.SHORT:
                                                    return (
                                                        <FormContentShort question={editingQuestion} />
                                                    )
                                                case QuestionType.LONG:
                                                    return (
                                                        <FormContentLong question={editingQuestion} />
                                                    )
                                                case QuestionType.MULTIPLE:
                                                    return (
                                                        <FormContentMultiple question={editingQuestion} />
                                                    )      
                                                case QuestionType.DATE:
                                                    return (
                                                        <FormContentDate question={editingQuestion} />
                                                    )
                                                case QuestionType.EMAIL:
                                                    return (
                                                        <FormContentEmail question={editingQuestion} />
                                                    )  
                                                case QuestionType.FILE_UPLOAD:
                                                    return (
                                                        <FormContentShort question={editingQuestion} />
                                                    )
                                                case QuestionType.WEBSITE:
                                                    return (
                                                        <FormContentWebsite question={editingQuestion} />
                                                    )
                                                case QuestionType.NUMBER:
                                                    return (
                                                        <FormContentNumber question={editingQuestion} />
                                                    )
                                                case QuestionType.PHONE:
                                                    return (
                                                        <FormContentPhone question={editingQuestion} />
                                                    )
                                                case QuestionType.CONTACT:
                                                    return (
                                                        <FormContentContact question={editingQuestion} />
                                                    )
                                                case QuestionType.NPS:
                                                    return (
                                                        <FormContentNPS question={editingQuestion} />
                                                    )
                                                case QuestionType.SCORE:
                                                    return (
                                                        <FormContentScore question={editingQuestion} />
                                                    )
                                                case QuestionType.RATING:
                                                    return (
                                                        <FormContentRating question={editingQuestion} />
                                                    )
                                                case QuestionType.SECTION:
                                                    return (
                                                        <FormContentSection question={editingQuestion} />
                                                    )    
                                                case QuestionType.END:
                                                    return (
                                                        <FormContentEnd question={editingQuestion} />
                                                    )
                                                default:
                                                    return (
                                                        <div 
                                                            className="form-content-zero"
                                                            onClick={()=> 
                                                                dispatch(setUIStates({
                                                                    ...uiStates,
                                                                    questionPickerOpen: true
                                                                }))
                                                            }
                                                        >
                                                            <div className="form-content-zero-image">
                                                                <img src={iconPath(ICONS.ADD, storeUser)} />
                                                            </div>
                                                            <div className="form-content-zero-title">
                                                                Add a Question
                                                            </div>
                                                        </div>
                                                    );
                                            }
                                        }
                                    )()}
                                </div>
                            </div>
                        </div>

                        {uiStates.commentMode && 
                            <div className="response-content-comment-blocker">

                            </div>
                        }
                    {/* </div> */}

                    {editingQuestion && uiStates.viewingComment && commentXP !== undefined 
                        && commentYP !== undefined &&
                        <div 
                            className="response-content-comment"
                            style={{top: `${commentYP}%`, left: `${commentXP}%`}}
                        >
                            <Comment 
                                user={storeUser}
                                form={editingForm}
                                comment={selectedComment}
                                comments={editingQuestion.comments}
                                questionId={editingQuestion.id}
                                xp={commentXP}
                                yp={commentYP}
                                onClose={() => {
                                    dispatch(setUIStates({
                                        ...uiStates,
                                        viewingComment: false
                                    }));
                                }}
                            />
                        </div>
                    }

                    {editingQuestion && uiStates.commentMode && parentComments.length > 0 
                        && parentComments.map((comment) => {
                            const publicDetails : PublicDetails | undefined = 
                                    (comment.publicName && comment.publicEmail) ? {
                                        publicName: comment.publicName,
                                        publicEmail: comment.publicEmail
                                    } : undefined;
                            
                            const adjustedX = (comment.xpercent / 100) * containerSize.width;
                            const adjustedY = (comment.ypercent / 100) * containerSize.height;

                            return (
                                <div 
                                    key={"comment-" + comment.id}
                                    className="response-content-comment-marker"
                                    style={{top: `${adjustedY}px`, left: `${adjustedX}px`}}
                                    onClick={(e: any)=> {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleOpenComment(comment)
                                    }}
                                    onMouseEnter={()=>handleCommentMouseEnter(comment)}
                                    onMouseLeave={handleCommentMouseLeave}
                                >   
                                    <CommentAvatar publicDetails={publicDetails} user={comment.user}/>
                                </div>
                            )
                        })
                    }
                </div>
            </div>
            {editingQuestion && !uiStates.commentMode &&
                <div 
                    className="form-content-layout"
                    onClick={() => dispatch(setUIStates({
                        ...uiStates, 
                        commentMode: false,
                        mobileLayout: !uiStates.mobileLayout
                    }))}
                >
                    {!uiStates?.mobileLayout &&
                        <Smartphone size={16} />
                    }
                    {uiStates?.mobileLayout &&
                        <Laptop size={16}/>
                    }
                </div>      
            } 
        </>  
    )
}